import { useState } from 'react';

import { CheckCircleOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { OrganizationCodes, getOrganizationLogoForAll, getAllBankName } from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { Button, Dropdown, Input, Tag, Typography } from 'antd';
import Image from 'next/image';

import { SearchReceiverAccountListItem, SearchReceiverAccountType } from '~apis/wiring';
import { useGetSearchReceiverAccounts } from '~hooks/queries/wiring/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';

import { WireListItem } from './types';

export type AccountInfo = {
  accountId: number;
  organizationCode: OrganizationCodes;
  accountNumber: string;
  alias: string;
  type: SearchReceiverAccountType;
  recentlyUsed: boolean;
};

const getAccountTypeText = (type: SearchReceiverAccountType) => {
  switch (type) {
    case 'BUSINESS_ENTITY':
      return '거래처 계좌';
    case 'SELF':
      return '내 계좌';
    default:
      return '';
  }
};

const formatOptions = (options: SearchReceiverAccountListItem[]) => {
  return options.map((option) => ({
    label: (
      <Flex align="center" justify="space-between">
        <Flex gap={8} align="center">
          <Image
            width={20}
            height={20}
            src={
              getOrganizationLogoForAll(option.bankCode)?.x2 ||
              'https://static.clobe.ai/image/etc-bank.png'
            }
            alt={option.bankCode}
          />
          <Typography.Text>{option.accountHolderName}</Typography.Text>
          <Typography.Text>{getAllBankName(option.bankCode)}</Typography.Text>
          <Typography.Text>{option.accountNumber}</Typography.Text>
          {option.type !== 'NONE' && <Tag>{getAccountTypeText(option.type)}</Tag>}
          {option.recentSent && (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              최근 보냄
            </Tag>
          )}
        </Flex>
        <Button
          type="link"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          새 행 입력
          <RightOutlined />
        </Button>
      </Flex>
    ),
    key: option.accountNumber + option.type + option.businessEntityId,
  }));
};

interface Props {
  onAccountSelect: (account: SearchReceiverAccountListItem) => void;
  selectedList?: WireListItem[];
}

const FavoriteAccountSelect: React.FC<Props> = ({ onAccountSelect, selectedList }) => {
  const { companyId } = useDemoAwareCompanyId();
  const [keyword, setKeyword] = useState('');
  const { data } = useGetSearchReceiverAccounts({
    companyId,
    keyword,
  });

  const accounts = data?.Data?.data?.items || [];

  const selected = selectedList?.map((item) => item.accountNumber);

  const filteredAccounts = accounts.filter((account) => !selected?.includes(account.accountNumber));

  return (
    <Dropdown
      menu={{
        items: formatOptions(filteredAccounts),
        onClick: (data) => {
          const selectedAccount = accounts.find(
            (account) =>
              account.accountNumber + account.type + account.businessEntityId === data.key,
          );

          if (!selectedAccount) return;
          onAccountSelect(selectedAccount);
        },
      }}
    >
      <Input
        prefix={<SearchOutlined />}
        onChange={(e) => setKeyword(e.target.value)}
        value={keyword}
        placeholder="거래처 계좌, 최근 보낸 계좌, 내 계좌를 검색하세요."
      />
    </Dropdown>
  );
};

export default FavoriteAccountSelect;
